// src/components/match/ParentSummaryModal.jsx
'use client';

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { X, Copy, RefreshCw, Sparkles, Check, Languages, Send } from 'lucide-react';

import { useAuth } from '@/context/AuthContext';

const SCRIMLY_BLUE = '#3483FE';

/**
 * Modal for the auto-generated post-match parent summary.
 *
 * Calls the `generateMatchSummary` Cloud Function, which returns
 * { summaryEn, summaryEs }. The modal defaults to the user's preferred
 * language but lets the coach toggle tabs to grab the other version.
 *
 * Pass ONE of:
 *   - `matchId` — modal calls the function with a Firestore match ref
 *   - `rawInputs` — { teamName, opponentName, ourScore, theirScore, sport?, dateStr?, notes? }
 *     for ad-hoc recaps with no match in Firestore (used by /recap)
 *   - `preloaded` — { summaryEn, summaryEs } to display a recap already in history
 *     without hitting the function again. Regenerate is hidden.
 */
export default function ParentSummaryModal({
  open,
  onClose,
  matchId,
  rawInputs,
  preloaded,
  teamName,
}) {
  const { t, i18n } = useTranslation();
  const { userProfile } = useAuth();

  const coachName = useMemo(() => {
    const first = userProfile?.firstName || '';
    const last = userProfile?.lastName || '';
    const display = userProfile?.displayName || '';
    return (first || last ? `${first} ${last}`.trim() : display) || '';
  }, [userProfile?.firstName, userProfile?.lastName, userProfile?.displayName]);

  const preferredLang = useMemo(() => {
    const raw = userProfile?.language || userProfile?.locale || i18n.language || 'en';
    return String(raw).toLowerCase().startsWith('es') ? 'es' : 'en';
  }, [userProfile?.language, userProfile?.locale, i18n.language]);

  const [activeLang, setActiveLang] = useState(preferredLang);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ summaryEn: '', summaryEs: '' });
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Stable signature for the "what changed" dep — rawInputs is an object
  // so we hash its meaningful fields rather than referencing the object itself.
  const rawKey = useMemo(() => {
    if (!rawInputs) return '';
    const {
      teamName: tn,
      opponentName,
      ourScore,
      theirScore,
      sport,
      dateStr,
    } = rawInputs;
    return [tn, opponentName, ourScore, theirScore, sport, dateStr]
      .map((v) => (v == null ? '' : String(v)))
      .join('|');
  }, [rawInputs]);

  const preloadedKey = useMemo(() => {
    if (!preloaded) return '';
    return `${preloaded.summaryEn || ''}||${preloaded.summaryEs || ''}`;
  }, [preloaded]);

  // Reset + generate when the modal opens for a given match / input set
  useEffect(() => {
    if (!open) return;
    setActiveLang(preferredLang);
    setError('');
    setCopied(false);

    // Short-circuit: preloaded summaries skip the Cloud Function entirely.
    if (preloaded && (preloaded.summaryEn || preloaded.summaryEs)) {
      setSummary({
        summaryEn: preloaded.summaryEn || '',
        summaryEs: preloaded.summaryEs || '',
      });
      setLoading(false);
      return;
    }

    setSummary({ summaryEn: '', summaryEs: '' });
    void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, matchId, rawKey, preloadedKey]);

  const generate = useCallback(async () => {
    if (!matchId && !rawInputs) return;
    setLoading(true);
    setError('');
    try {
      const functions = getFunctions();
      const call = httpsCallable(functions, 'generateMatchSummary');
      const payload = matchId
        ? { matchId, coachName }
        : { rawInputs, coachName };
      const resp = await call(payload);
      const data = resp?.data || {};
      setSummary({
        summaryEn: data.summaryEn || '',
        summaryEs: data.summaryEs || '',
      });
    } catch (err) {
      console.error('[ParentSummaryModal] generate failed', err);
      setError(err?.message || t('match.parentSummary.errorGeneric'));
    } finally {
      setLoading(false);
    }
  }, [matchId, rawInputs, coachName, t]);

  const currentBody =
    activeLang === 'es' ? summary.summaryEs : summary.summaryEn;

  const handleShareWhatsApp = useCallback(() => {
    if (!currentBody) return;
    const url = `https://wa.me/?text=${encodeURIComponent(currentBody)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [currentBody]);

  const handleCopy = useCallback(async () => {
    if (!currentBody) return;
    try {
      await navigator.clipboard.writeText(currentBody);
      setCopied(true);
      toast.success(t('match.parentSummary.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for very old browsers
      const el = document.createElement('textarea');
      el.value = currentBody;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
        toast.success(t('match.parentSummary.copied'));
      } catch {
        toast.error('Could not copy to clipboard.');
      }
      document.body.removeChild(el);
    }
  }, [currentBody, t]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t('match.parentSummary.title')}
    >
      <div className="bg-white dark:bg-zinc-900 w-full sm:max-w-xl rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-zinc-100 dark:border-zinc-800">
          <div className="flex items-start gap-3">
            <div
              className="p-2 rounded-xl flex-shrink-0"
              style={{
                backgroundColor: 'rgba(52,131,254,0.10)',
                color: SCRIMLY_BLUE,
              }}
            >
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-zinc-900 dark:text-white">
                {t('match.parentSummary.title')}
              </h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                {t('match.parentSummary.subtitle', {
                  teamName: teamName || 'your team',
                })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Language tabs */}
        <div className="flex gap-1 px-6 pt-4">
          {[
            { code: 'en', label: t('match.parentSummary.tabEn') },
            { code: 'es', label: t('match.parentSummary.tabEs') },
          ].map((tab) => {
            const active = activeLang === tab.code;
            return (
              <button
                key={tab.code}
                type="button"
                onClick={() => setActiveLang(tab.code)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'
                }`}
              >
                <Languages className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-zinc-500">
              <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin" />
              <p className="text-sm font-medium">
                {t('match.parentSummary.generating')}
              </p>
            </div>
          )}

          {!loading && error && (
            <div className="bg-rose-50 border border-rose-100 text-rose-700 rounded-xl p-4 text-sm">
              {error}
            </div>
          )}

          {!loading && !error && currentBody && (
            <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-xl p-4 border border-zinc-100 dark:border-zinc-800">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-800 dark:text-zinc-100">
                {currentBody}
              </p>
            </div>
          )}

          {!loading && !error && (
            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-3 leading-relaxed">
              {t('match.parentSummary.disclaimer')}
            </p>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-zinc-100 dark:border-zinc-800 px-6 py-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
          {!preloaded && (
            <button
              type="button"
              onClick={generate}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-zinc-700 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {t('match.parentSummary.regenerate')}
            </button>
          )}
          <button
            type="button"
            onClick={handleShareWhatsApp}
            disabled={loading || !currentBody}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#25D366] hover:bg-[#20BD5A] transition-colors disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {t('match.parentSummary.shareWhatsApp', 'WhatsApp')}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={loading || !currentBody}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
            style={{
              backgroundColor: SCRIMLY_BLUE,
              boxShadow: '0 10px 24px rgba(52,131,254,0.20)',
            }}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                {t('match.parentSummary.copied')}
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                {t('match.parentSummary.copy')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
